import * as React from 'react';
import { Controller, useWatch, type Control, type FieldValues, type UseFormSetValue } from 'react-hook-form';

import { getSemanticAttributes } from '../internals/semanticProps';
import { SearchPicker, type SearchPickerOption } from '../composites/SearchPicker';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfLayoutProps } from './rhfLayout';
import type { Effect } from './reactions';
import { useEffects } from './useEffects';
import { useReactions } from './useReactions';
import {
  useAsyncOptions,
  type DataSource,
} from './RhfAsyncSelect';

export interface RhfSearchPickerProps extends RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  dataSource?: DataSource;
  placeholder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  hint?: string;
  semanticId?: string;
  disabled?: boolean;
  isClearable?: boolean;
  /**
   * Panel option grid columns. SearchPicker-only — never confuse with matrix minCols/maxCols.
   * @default 2
   */
  columns?: number;
  /** Domain field key used to populate option.subtitle (e.g. `employeeId`). */
  subtitleField?: string;
  /** Prefix for subtitle display (e.g. `#` → `#111`). @default '#' when subtitleField is set */
  subtitlePrefix?: string;
  debounceMs?: number;
  noOptionsMessage?: string;
  dataSourceParams?: Record<string, string>;
  resetOnParamsChange?: boolean;
  enabled?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  effects?: Effect[];
  onOptionChange?: (value: string, option: Record<string, unknown>) => void;
  controlWidth?: RhfLayoutProps['controlWidth'];
}

function withSubtitles(
  options: Array<SearchPickerOption & Record<string, unknown>>,
  subtitleField: string | undefined,
): SearchPickerOption[] {
  if (!subtitleField) return options;
  return options.map((opt) => {
    if (opt.subtitle != null && String(opt.subtitle).length > 0) return opt;
    const raw = opt[subtitleField];
    if (raw == null || String(raw).length === 0) return opt;
    return { ...opt, subtitle: String(raw) };
  });
}

/**
 * RHF adapter for {@link SearchPicker} — Participant Name panel picker.
 * Reuses AsyncSelect dataSource / React Query loading; swaps chrome to SearchPicker.
 */
export const RhfSearchPicker = React.forwardRef<HTMLDivElement, RhfSearchPickerProps>(
  function RhfSearchPicker(
    {
      control,
      name,
      label,
      dataSource,
      placeholder = 'Select',
      searchPlaceholder = 'Search',
      required = false,
      hint,
      semanticId,
      disabled = false,
      isClearable = true,
      columns = 2,
      subtitleField,
      subtitlePrefix,
      debounceMs = 300,
      noOptionsMessage,
      dataSourceParams,
      resetOnParamsChange = false,
      enabled = true,
      setValue,
      effects,
      onOptionChange,
      controlWidth,
    },
    ref,
  ) {
    const overrides = useReactions(control, undefined);
    const { runEffects } = useEffects(effects, setValue);

    const finalDisabled = overrides.disabled ?? disabled;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;
    const finalEnabled = (overrides.enabled as boolean) ?? enabled;

    if (overrides.visible === false) return null;

    const watchedFieldNames = React.useMemo(
      () => (dataSourceParams ? Object.values(dataSourceParams) : []),
      [dataSourceParams],
    );
    const watchedValues = useWatch({ control, name: watchedFieldNames as string[] });

    const resolvedParams = React.useMemo(() => {
      if (!dataSourceParams) return undefined;
      const params: Record<string, string | number | boolean> = {};
      const paramEntries = Object.entries(dataSourceParams);
      paramEntries.forEach(([apiParam, _fieldName], index) => {
        const value = Array.isArray(watchedValues) ? watchedValues[index] : watchedValues;
        if (value != null && value !== '') {
          params[apiParam] = value as string | number | boolean;
        }
      });
      return Object.keys(params).length > 0 ? params : undefined;
    }, [dataSourceParams, watchedValues]);

    const resolvedParamsKey = React.useMemo(
      () => (resolvedParams ? JSON.stringify(resolvedParams) : ''),
      [resolvedParams],
    );

    const isHydratedRef = React.useRef(false);
    const prevParamsKeyRef = React.useRef(resolvedParamsKey);

    React.useEffect(() => {
      if (!isHydratedRef.current) {
        isHydratedRef.current = true;
        prevParamsKeyRef.current = resolvedParamsKey;
        return;
      }
      if (resetOnParamsChange && setValue && prevParamsKeyRef.current !== resolvedParamsKey) {
        setValue(name, '');
      }
      prevParamsKeyRef.current = resolvedParamsKey;
    }, [resolvedParamsKey, resetOnParamsChange, setValue, name]);

    const cascadeReady = !dataSourceParams || resolvedParams != null;

    const { allOptions, isInitialLoading } = useAsyncOptions({
      dataSource,
      debounceMs,
      enabled: finalEnabled && cascadeReady,
      resolvedParams,
    });

    const pickerOptions = React.useMemo(
      () => withSubtitles(allOptions as SearchPickerOption[], subtitleField),
      [allOptions, subtitleField],
    );

    const resolvedSubtitlePrefix =
      subtitlePrefix ?? (subtitleField ? '#' : '');

    const currentValue = useWatch({ control, name });
    const hydrationDoneRef = React.useRef(false);

    React.useEffect(() => {
      if (!onOptionChange || hydrationDoneRef.current) return;
      if (!currentValue || pickerOptions.length === 0) return;
      const matched = pickerOptions.find((o) => o.value === String(currentValue));
      if (matched) {
        onOptionChange(String(currentValue), matched as unknown as Record<string, unknown>);
        hydrationDoneRef.current = true;
      }
    }, [currentValue, pickerOptions, onOptionChange]);

    const semantic = getSemanticAttributes(semanticId ?? name);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          return (
            <FormField
              ref={ref}
              label={finalLabel}
              hint={hint}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <div {...semantic} style={{ width: '100%', maxWidth: '100%' }}>
                <SearchPicker
                  name={name}
                  semanticId={semanticId ?? name}
                  options={pickerOptions}
                  value={field.value == null || field.value === '' ? null : String(field.value)}
                  onChange={(next, option) => {
                    field.onChange(next ?? '');
                    runEffects(next ?? '');
                    if (onOptionChange) {
                      if (option) {
                        onOptionChange(option.value, option as unknown as Record<string, unknown>);
                      } else {
                        onOptionChange('', {});
                      }
                    }
                  }}
                  onBlur={field.onBlur}
                  placeholder={placeholder}
                  searchPlaceholder={searchPlaceholder}
                  disabled={finalDisabled}
                  loading={isInitialLoading}
                  error={Boolean(errorText)}
                  clearable={isClearable}
                  columns={columns}
                  subtitlePrefix={resolvedSubtitlePrefix}
                  emptyMessage={noOptionsMessage ?? 'No Data Found'}
                />
              </div>
            </FormField>
          );
        }}
      />
    );
  },
);

RhfSearchPicker.displayName = 'RhfSearchPicker';
